// Клиент `/api/v1/agent_tool_allowlist`.
//
// Управление per-(org, role) allow-list'ом инструментов из UI «Настройки →
// Разрешённые инструменты». Зеркалит pydantic-схемы backend'а
// (`ToolListEntryResponse` / `AddToolRequest`). JWT прикручивает
// `authedFetch`; org берётся из токена (RLS), не из тела.
//
// Список отдаётся голым массивом, без обёртки `{items}`. Запись
// адресуется парой `(role_name, tool_name)`: `DELETE` несёт оба в query,
// без параметрического пути. `POST` отвечает 200 и на создание, и на
// повтор; `DELETE` — 204 без тела и идемпотентно (не 404, если записи уже
// не было; 404 — только незарегистрированная роль).

import { authedFetch } from "../../auth/authedFetch";

// Источник разрешения: `manual` (правка человеком) или `self_improvement`
// (применение предложения `tool_allowlist`). Держим типом для Badge.
export type ToolAllowlistSource = "manual" | "self_improvement";

export interface ToolAllowlistItem {
  readonly kind: "allowlist";
  readonly role_name: string;
  readonly tool_name: string;
  readonly source: ToolAllowlistSource;
  // Известен ли tool_name backend'у (`is_known_tool`) — имя не
  // валидируется структурно, опечатку UI показывает через этот флаг.
  readonly known: boolean;
}

export interface AddToolAllowlistItemRequest {
  readonly role_name: string;
  readonly tool_name: string;
}

/**
 * HTTP-ошибка с числовым `status`. Секция не парсит body и не
 * различает коды — `status` несёт только `message` (`"<fn>: HTTP
 * <status>"`), показанный пользователю через `onError` мутации как
 * есть. Зеркало `toolBlacklist.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function fetchToolAllowlist(
  roleName: string,
): Promise<readonly ToolAllowlistItem[]> {
  const query = new URLSearchParams({ role_name: roleName });
  const response = await authedFetch(
    `/api/v1/agent_tool_allowlist?${query.toString()}`,
    { headers: buildHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `fetchToolAllowlist: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as readonly ToolAllowlistItem[];
}

export async function addToolAllowlistItem(
  body: AddToolAllowlistItemRequest,
): Promise<ToolAllowlistItem> {
  const response = await authedFetch("/api/v1/agent_tool_allowlist", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `addToolAllowlistItem: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ToolAllowlistItem;
}

export async function removeToolAllowlistItem(
  roleName: string,
  toolName: string,
): Promise<void> {
  const query = new URLSearchParams({
    role_name: roleName,
    tool_name: toolName,
  });
  const response = await authedFetch(
    `/api/v1/agent_tool_allowlist?${query.toString()}`,
    { method: "DELETE", headers: buildHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `removeToolAllowlistItem: HTTP ${String(response.status)}`,
    );
  }
}
